import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import contractService from '../../services/contractService';
import ContractRenewalModal from '../../components/features/contracts/ContractRenewalModal';
import { getContractStatus, CONTRACT_STATUSES } from '../../constants/statuses';

import AddContractModal from '../../components/features/contracts/AddContractModal';
import EditContractModal from '../../components/features/contracts/EditContractModal';
import ViewContractModal from '../../components/features/contracts/ViewContractModal'; // We'll create this

const ContractsView = () => {
  const [contracts, setContracts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const p = searchParams.get('status');
    if (p === 'EXPIRED') return ['Expired'];
    if (p === 'ACTIVE') return ['Active', 'Near Expiry'];
    return ['Active', 'Near Expiry'];
  });
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);

  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    filterContracts();
  }, [searchTerm, selectedStatuses, contracts]);

  const loadContracts = async () => {
    setLoading(true);
    const result = await contractService.getAllContracts();
    if (result.success) {
      setContracts(result.data);
      setFilteredContracts(result.data);
    } else {
      error(result.error || 'Failed to load Contracts');
    }
    setLoading(false);
  };

  const filterContracts = () => {
    let filtered = [...contracts];

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.company?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.site?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered = filtered.filter(c => {
      const status = getContractStatus(c);
      return selectedStatuses.includes(status);
    });

    setFilteredContracts(filtered);
  };

  const handleAddContract = async (contractData) => {
    const result = await contractService.createContract(contractData);
    if (result.success) {
      success('Contract added successfully!');
      await loadContracts();
    } else {
      error(result.error || 'Failed to add Contract');
    }
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setShowViewModal(true);
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setShowEditModal(true);
  };

  const handleUpdateContract = async (contractId, contractData) => {
    const result = await contractService.updateContract(contractId, contractData);
    if (result.success) {
      success('Contract updated successfully!');
      await loadContracts();
    } else {
      error(result.error || 'Failed to update Contract');
    }
  };

  const handleRenewContract = (contract) => {
    setSelectedContract(contract);
    setShowRenewModal(true);
  };

  const handleRenewSubmit = async (contractId, renewalData) => {
    const result = await contractService.renewContract(contractId, renewalData);
    if (result.success) {
      success('Contract renewed successfully!');
      await loadContracts();
    } else {
      error(result.error || 'Failed to renew Contract');
    }
  };

  const handleToggleEnabled = async (contract) => {
    const nextValue = !(contract.is_enabled !== undefined ? contract.is_enabled : contract.status !== 'Disabled');
    const result = await contractService.toggleContractStatus(contract.id, nextValue);
    if (result.success) {
      success(`Contract ${nextValue ? 'enabled' : 'disabled'} successfully!`);
      await loadContracts();
    } else {
      error(result.error || 'Failed to update contract status');
    }
  };

  const handleExportContracts = async () => {
    const statusParam = selectedStatuses.includes('Expired') && selectedStatuses.length === 1 ? 'INACTIVE' : (selectedStatuses.includes('Active') || selectedStatuses.includes('Near Expiry') ? 'ACTIVE' : undefined);
    const result = await contractService.exportContractsCSV({ status: statusParam });
    if (result.success) {
      success('Contracts exported successfully!');
    } else {
      error(result.error || 'Failed to export contracts');
    }
  };

  const handleDeleteContract = (contract) => {
    setSelectedContract(contract);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await contractService.deleteContract(selectedContract.id);
    if (result.success) {
      success('Contract deleted successfully!');
      await loadContracts();
    } else {
      error(result.error || 'Failed to delete Contract');
    }
    setShowDeleteDialog(false);
    setSelectedContract(null);
  };

  const getContractStats = () => {
    const total = filteredContracts.length;
    const active = filteredContracts.filter(c => {
      const status = getContractStatus(c);
      return status === CONTRACT_STATUSES.ACTIVE || status === CONTRACT_STATUSES.NEAR_EXPIRY;
    }).length;
    const inactive = filteredContracts.filter(c => getContractStatus(c) === CONTRACT_STATUSES.EXPIRED).length;
    const totalValue = filteredContracts.reduce((sum, c) => sum + (Number(c.value) || 0), 0);
    return { total, active, inactive, totalValue };
  };

  const stats = getContractStats();

  const handleContractCardClick = (filter) => {
    setSearchTerm('');
    if (filter === 'ACTIVE') {
      setSelectedStatuses(['Active', 'Near Expiry']);
      setSearchParams({ status: 'ACTIVE' });
    } else if (filter === 'INACTIVE') {
      setSelectedStatuses(['Expired']);
      setSearchParams({ status: 'EXPIRED' });
    } else {
      setSelectedStatuses(['Active', 'Near Expiry', 'Expired']);
      setSearchParams({});
    }
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const columns = [
    {
      key: 'po_number',
      label: 'PO Number',
      render: (value) => <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{value}</span>
    },
    {
      key: 'company',
      label: 'Company',
      render: (value, row) => row.company?.company_name || row.company_name || 'Unassigned'
    },
    {
      key: 'site_location',
      label: 'Site & Location',
      render: (value, row) => {
        const siteName = row.site?.name || row.site_name || 'Unassigned';
        const location = row.site?.location || row.location || '';
        return (
          <div>
            <div style={{ fontWeight: '500' }}>{siteName}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{location}</div>
          </div>
        );
      }
    },
    {
      key: 'type',
      label: 'Type',
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          background: value === 'Comprehensive' ? '#10b98120' : '#f59e0b20',
          color: value === 'Comprehensive' ? '#10b981' : '#f59e0b'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'value',
      label: 'Value',
      render: (value) => `₹${Number(value).toLocaleString()}`
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value, row) => (
        <div style={{ fontSize: '13px' }}>
          <div><strong>Start:</strong> {new Date(row.start_date).toLocaleDateString()}</div> 
          <div><strong>End:</strong> {new Date(row.end_date).toLocaleDateString()}</div>
        </div>
      )
    },
    // {
    //   key: 'services',
    //   label: 'Services',
    //   render: (value) => {
    //     const svcList = value || [];
    //     if (svcList.length === 0) return 'None';
    //     return (
    //       <div style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }}>
    //         {svcList.join(', ')}
    //       </div>
    //     );
    //   }
    // },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => {
        const status = getContractStatus(row);
        let bg = '#10b98120';
        let fg = '#10b981';
        if (status === CONTRACT_STATUSES.NEAR_EXPIRY) {
          bg = '#f59e0b20';
          fg = '#f59e0b';
        } else if (status === CONTRACT_STATUSES.EXPIRED) {
          bg = '#ef444420';
          fg = '#ef4444';
        }
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            background: bg,
            color: fg
          }}>
            {status}
          </span>
        );
      }
    },
    {
      key: 'is_enabled',
      label: 'Enabled',
      sortable: false,
      render: (value, row) => {
        const isEnabled = row.expired ? false : (value !== undefined ? value : row.status !== 'Disabled');
        return (
          <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={() => handleToggleEnabled(row)}
              style={{ display: 'none' }}
            />
            <span
              style={{
                width: '42px',
                height: '24px',
                borderRadius: '999px',
                background: isEnabled ? '#10b981' : '#9ca3af',
                position: 'relative',
                transition: 'all 0.2s ease'
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '3px',
                  left: isEnabled ? '22px' : '3px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  transition: 'left 0.2s ease'
                }}
              />
            </span>
          </label>
        );
      }
    }
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>Loading Contracts...</div>;
  }

  // Delete Warning dynamically configured based on requirements
  const isPending = selectedContract?.status === 'Pending';
  const hasSite = !!selectedContract?.site_id;
  
  let deleteMessage = `Are you sure you want to delete Contract ${selectedContract?.po_number}? This action cannot be undone.`;
  if (isPending || hasSite) {
    const reasons = [];
    if (isPending) reasons.push("is currently Pending");
    if (hasSite) reasons.push("is attached to a Site");
    deleteMessage = `Warning: Contract ${selectedContract?.po_number} ${reasons.join(' and ')}. Please detach it or resolve its status before deleting.`;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
      ))}

      <div style={gridStyle}>
        <StatCard title="Total Contracts" value={stats.total} icon="" color="#3b82f6" onClick={() => handleContractCardClick('ALL')} />
        <StatCard title="Active Contracts" value={stats.active} icon="" color="#10b981" onClick={() => handleContractCardClick('ACTIVE')} />
        <StatCard title="Expired / Inactive" value={stats.inactive} icon="" color="#ef4444" onClick={() => handleContractCardClick('INACTIVE')}/>
        <StatCard title="Total Value" value={`₹${(stats.totalValue / 100000).toFixed(2)}L`} icon="" color="#8b5cf6" subtitle="Cumulative value" />
      </div>

      {/* Multi-select Status Filters */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: '600', marginRight: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>Status Filters:</span>
        {Object.values(CONTRACT_STATUSES).map((status) => {
          const isSelected = selectedStatuses.includes(status);
          return (
            <button
              key={status}
              onClick={() => {
                setSelectedStatuses(prev => {
                  if (prev.includes(status)) {
                    if (prev.length === 1) return prev; // Keep at least one filter selected
                    return prev.filter(s => s !== status);
                  } else {
                    return [...prev, status];
                  }
                });
              }}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid var(--border)',
                background: isSelected ? 'var(--primary)' : 'var(--surface-light)',
                color: isSelected ? '#ffffff' : 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {status === CONTRACT_STATUSES.ACTIVE && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isSelected ? '#ffffff' : '#10b981' }} />}
              {status === CONTRACT_STATUSES.NEAR_EXPIRY && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isSelected ? '#ffffff' : '#f59e0b' }} />}
              {status === CONTRACT_STATUSES.EXPIRED && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isSelected ? '#ffffff' : '#ef4444' }} />}
              {status}
            </button>
          );
        })}
      </div>

      <div style={filterBarStyle}>
        <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Input
            placeholder="Search contracts by PO ID, Company or Site..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" onClick={loadContracts}>Refresh</Button>
          <Button variant="secondary" onClick={handleExportContracts}>Export CSV</Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>Add Contract</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredContracts}
        actions={[
          { label: ' View', onClick: handleViewContract },
          {
            label: ' Edit',
            onClick: handleEditContract,
            condition: (row) => {
              const isPastEnd = new Date(row.end_date) < new Date();
              return !row.expired && !isPastEnd;
            }
          },
          {
            label: ' Renew',
            onClick: handleRenewContract,
            condition: (row) => {
              const status = getContractStatus(row);
              return (status === CONTRACT_STATUSES.NEAR_EXPIRY || status === CONTRACT_STATUSES.EXPIRED) && !row.expired;
            }
          },
          { label: ' Delete', onClick: handleDeleteContract }
        ]}
        pageSize={15}
      />

      <AddContractModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddContract}
      />

      <ViewContractModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />

      <EditContractModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContract(null);
        }}
        onSubmit={handleUpdateContract}
        contract={selectedContract}
      />

       <ContractRenewalModal
        isOpen={showRenewModal}
        onClose={() => {
          setShowRenewModal(false);
          setSelectedContract(null);
        }}
        onSubmit={handleRenewSubmit}
        contract={selectedContract}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedContract(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Contract"
        message={deleteMessage}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default ContractsView;